import { memo as Memo, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { gsap } from 'gsap';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';

//* Helpers
import breakString from '@helpers/breakString';

//* Components
import Text from '../Text';
import Ziggy from '@components/global/SvgComponents/Ziggy';
import Sharpy from '@components/global/SvgComponents/Sharpy';
import Rolly from '@components/global/SvgComponents/Rolly';
import Peezzy from '@components/global/SvgComponents/Peezzy';
import GameService from '../SvgComponents/Game';
import AppService from '../SvgComponents/App';
import SeoService from '../SvgComponents/Seo';

//* JSONs
import config from '@lib/config.json';

//* Styles
import PageTopSectionStyle from './style';
import Button from '../Button';
import withLanguageContext from '@/context/consumerHOC/LanguageConsumer';
import { usePathname, useRouter } from 'next/navigation';

const PageTopSection = Memo(({ className, innerTitle, isSingleService = false, titleTag, translate, ...props }) => {
	//! Router hooks
	const router = useRouter();
	const pathname = usePathname();

	//! Navigate back one slug level
	const handleBackNavigation = useCallback(() => {
		const pathSegments = pathname.split('/').filter(Boolean);
		// Remove the last segment (current subSlug) to go back one level
		const backPath = '/' + pathSegments.slice(0, -1).join('/');
		router.push(backPath);
	}, [router, pathname]);

	const AnimationComponent = useMemo(() => {
		switch (props.serviceType) {
			case 'Branding':
				return Peezzy;
			case 'E-commerce':
				return Ziggy;
			case 'Digital':
				return Sharpy;
			case 'Product':
				return Rolly;
			case 'Game':
				return GameService;
			case 'App':
				return AppService;
			case 'Seo':
				return SeoService;
			default:
				return Peezzy;
		}
	}, [props.serviceType]);

	//! Refs
	const topRef = useRef();
	const topRef1 = useRef();
	const topRef2 = useRef();
	const topRef3 = useRef();
	const topRef4 = useRef();
	const topRef5 = useRef();

	//! Timeline
	const tl = useMemo(() => gsap.timeline({ delay: 0.6, paused: true }), []);

	//! Init GSAP animation
	useLayoutEffect(() => {
		if (!topRef.current) return;

		const ctx = gsap.context(() => {
			const arr = [
				...(topRef1.current?.children || []),
				topRef5.current?.children?.[0],
				...(topRef5.current?.children?.[1]?.children || []),
				topRef2.current,
				topRef3.current,
				topRef4.current,
			].filter(Boolean);

			const arr1 = [topRef1.current?.children?.[1], topRef5.current?.children?.[1]?.children?.[1]].filter(Boolean);

			tl.clear().to(arr, { opacity: 1, duration: 0, stagger: 0.1 }).set(arr1, { fontStyle: 'italic', delay: 0.3 });
		}, topRef);

		return () => {
			ctx.revert();
			tl.kill();
		};
	}, [tl]);

	//! Scroll handler
	const sectionAnimation = useCallback(() => {
		if (!topRef.current) return;
		const elTop = topRef.current.getBoundingClientRect().top;

		if (elTop < props.winHeight) {
			tl.play();
		} else {
			tl.reverse();
		}
	}, [tl, props.winHeight]);

	useLayoutEffect(() => {
		sectionAnimation();
	}, []);

	//!  scroll
	useLayoutEffect(() => {
		window.addEventListener('scroll', sectionAnimation);
		return () => {
			window.removeEventListener('scroll', sectionAnimation);
		};
	}, [sectionAnimation]);

	return (
		<PageTopSectionStyle
			ref={topRef}
			className={`ContainerWrap ${props.settings?.min_title ? '' : 'contMarg'} ${className || ''} ${props.settings?.underline ? 'underline' : ''}`}>
			{props.title && !innerTitle && (
				<>
					{props.back ? (
						<div
							ref={topRef5}
							// className='servicesTitle'
							className={`${props.back ? 'back-button' : ''} servicesTitle`}>
							<div
								className='navigation-back'
								onClick={handleBackNavigation}>
								<Text
									className={'h7 text-back'}
									text={'back'}
								/>
							</div>
						</div>
					) : null}
					<Text
						tag={titleTag || 'h1'}
						ref={topRef1}
						className={`${props.settings?.min_title || isSingleService ? 'h3' : 'h1'}  font-poppins font-semi-bold title`}>
						{breakString(props.title)}
					</Text>
				</>
			)}

			{innerTitle && (
				<div className='servicesTop'>
					{props.serviceType ? (
						<div className={`servicesAnimationWrap ${props.serviceType === 'E-commerce' ? 'case-ziggy' : ''}`}>
							<AnimationComponent />
						</div>
					) : null}

					<div
						ref={topRef5}
						// className='servicesTitle'
						className={`${props.back ? 'back-button' : ''} servicesTitle`}>
						{props.back ? (
							<div
								className='navigation-back'
								onClick={handleBackNavigation}>
								<Text
									className={'h7 text-back'}
									text={'back'}
								/>
							</div>
						) : (
							<Text className='h4 font-anonymous font-bold servicesCounter'>{'0' + innerTitle}</Text>
						)}
						<Text
							tag={titleTag || 'h2'}
							className={`${isSingleService ? 'h3 single-service-title' : 'h1'} ${props.font ? props.font : ''} font-poppins font-semi-bold titleOther `}>
							{breakString(props.title)}
						</Text>
					</div>
				</div>
			)}

			{props.subtitle && (
				<Text
					ref={topRef2}
					className='h5 font-poppins font-light subtitle'>
					{props.subtitle}
				</Text>
			)}

			{props.description && (
				<div className='position_right'>
					<Text
						ref={topRef3}
						className={`${props.settings?.short_size ? 'short' : 'long'} p-m font-poppins font-light description`}>
						{props.description}
					</Text>
				</div>
			)}

			{props.button ? (
				<div
					ref={topRef4}
					className={'button-top-wrap'}>
					<Button
						text={translate('clickHere')}
						otherLink={config.routes.default['contact-form'].path}
					/>
				</div>
			) : null}
		</PageTopSectionStyle>
	);
});

export default withLanguageContext(withUIContext(PageTopSection, ['winHeight']), ['translate']);
